import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext, AccessContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { NumericInput } from '../components/ui/numeric-input';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Shield, FileText, Users, Palette, Sun, Moon, Monitor, RotateCcw, Sparkles, Check, Building2, Zap, Droplets, Receipt, Plus, Trash2, AlertTriangle, Package, UserCog } from 'lucide-react';
import { brightPalettes, classicPalettes, colorPropertyLabels } from '../config/colorPalettes';
import UsersTab from '../components/UsersTab';

// Dealership Brand Themes
const dealershipThemes = [
  {
    id: 'none',
    name: 'Default (No Brand)',
    shortName: 'Default',
    primary: '#3B82F6',
    accent: '#60A5FA',
    text: '#FFFFFF',
    description: 'Standard theme without brand colors'
  },
  {
    id: 'iocl',
    name: 'Indian Oil (IOCL)',
    shortName: 'IOCL',
    primary: '#F37022',
    accent: '#02164F',
    text: '#FFFFFF',
    description: 'Vivid Tangelo Orange with Oxford Blue'
  },
  {
    id: 'bpcl',
    name: 'Bharat Petroleum (BPCL)',
    shortName: 'BPCL',
    primary: '#007BC9',
    accent: '#FFE000',
    text: '#FFFFFF',
    description: 'Lochmara Blue with Golden Yellow'
  },
  {
    id: 'hpcl',
    name: 'Hindustan Petroleum (HPCL)',
    shortName: 'HPCL',
    primary: '#0070B4',
    accent: '#D7AF64',
    text: '#FFFFFF',
    description: 'Cobalt Blue with Golden Brown'
  },
  {
    id: 'jiobp',
    name: 'Jio-bp (Reliance-bp)',
    shortName: 'Jio-bp',
    primary: '#15803D',
    accent: '#CAFF00',
    text: '#FFFFFF',
    description: 'Forest Green with Bright Lime'
  },
  {
    id: 'nayara',
    name: 'Nayara Energy',
    shortName: 'Nayara',
    primary: '#001F5B',
    accent: '#00AEEF',
    text: '#FFFFFF',
    description: 'Dark Navy Blue with Bright Cyan'
  },
  {
    id: 'shell',
    name: 'Shell India',
    shortName: 'Shell',
    primary: '#ED1C24',
    accent: '#FFD500',
    text: '#FFFFFF',
    description: 'Shell Red with Bright Yellow'
  }
];

// Page definitions with labels
const allPages = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'daily_operations', label: 'Daily Operations' },
  { id: 'quick_actions', label: 'Quick Actions' },
  { id: 'e_charging', label: 'E-Charging' },
  { id: 'lubricants', label: 'Lubricants' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'reports', label: 'Reports' },
  { id: 'audit_logs', label: 'Audit Logs' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'customers', label: 'Customers' },
  { id: 'payments', label: 'Payments' },
  { id: 'settings', label: 'Settings' },
  { id: 'pump_profile', label: 'Pump Profile' },
  { id: 'user_management', label: 'User Management' },
  { id: 'multi_station', label: 'Multi-Station' }
];

const userRoles = ['admin', 'manager', 'standard', 'viewer'];

const defaultPageAccess = {
  admin: ['dashboard', 'daily_operations', 'quick_actions', 'e_charging', 'lubricants', 'accounts', 'incidents', 'reports', 'audit_logs', 'alerts', 'customers', 'payments', 'settings', 'pump_profile', 'user_management', 'multi_station'],
  manager: ['dashboard', 'daily_operations', 'quick_actions', 'e_charging', 'lubricants', 'accounts', 'incidents', 'reports', 'audit_logs', 'alerts', 'payments'],
  standard: ['dashboard', 'daily_operations', 'quick_actions', 'e_charging', 'lubricants'],
  viewer: ['dashboard']
};

const defaultReportAccess = {
  admin: { can_view: true, can_download: true, months_allowed: 12 },
  manager: { can_view: true, can_download: true, months_allowed: 6 },
  standard: { can_view: true, can_download: false, months_allowed: 1 },
  viewer: { can_view: true, can_download: false, months_allowed: 1 }
};

// Display labels for roles
const roleLabels = {
  admin: 'Admin',
  manager: 'Manager',
  standard: 'Standard',
  viewer: 'Viewer'
};

function Settings() {
  const { user } = useContext(AuthContext);
  const { fetchAccessSettings } = useContext(AccessContext);
  const { 
    isDarkMode, 
    toggleDarkMode,
    setDarkMode,
    customColors, 
    isCustomized, 
    updateCustomColor, 
    applyPalette, 
    resetToDefault,
    dealership 
  } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('thresholds');
  const [colorPickerOpen, setColorPickerOpen] = useState(null);
  const [settings, setSettings] = useState({
    ms_stock_threshold: 500,
    hsd_stock_threshold: 1000,
    cash_collection_threshold: 50000,
    ms_loss_threshold: 50,
    hsd_loss_threshold: 50,
    ms_sell_price: 105.0,
    hsd_sell_price: 92.0,
    sales_day_start_time: '06:00',
    custom_payment_methods: [],
    page_access: defaultPageAccess,
    report_access: defaultReportAccess
  });
  
  // UI Preferences (stored in localStorage)
  const [uiPreferences, setUiPreferences] = useState(() => {
    const saved = localStorage.getItem('uiPreferences');
    return saved ? JSON.parse(saved) : {
      theme: 'system',
      dateFormat: 'DD/MM/YYYY',
      numberFormat: 'indian',
      defaultDashboardView: 'overview',
      compactMode: false,
      showChartAnimations: true
    };
  });

  // Dealership Brand Theme (stored in localStorage)
  const [selectedDealershipTheme, setSelectedDealershipTheme] = useState(() => {
    const saved = localStorage.getItem('dealershipBrandTheme');
    return saved || 'none';
  });

  // E-Charging Settings
  const [eChargingSettings, setEChargingSettings] = useState({
    enabled: false,
    price_per_kwh: 12.0,
    gst_rate: 18.0,
    idle_charge_per_min: 2.0,
    service_charge: 10.0,
    grid_cost_per_kwh: 8.0,
    charging_points: []
  });
  
  // Lubricant Settings
  const [lubricantSettings, setLubricantSettings] = useState({
    enabled: false
  });
  
  // Lubricant Products for threshold configuration
  const [lubricantProducts, setLubricantProducts] = useState([]);
  const [lubricantThresholds, setLubricantThresholds] = useState({});
  const [loadingLubricantProducts, setLoadingLubricantProducts] = useState(false);
  
  // Bill Settings
  const [billSettings, setBillSettings] = useState({
    bill_format: 'classic',
    show_fuelogic_logo: true,
    show_fuelogic_name: true,
    fuelogic_punchline: 'Smart Fuel Management',
    show_station_name: true,
    show_station_address: true,
    show_station_mobile: true,
    show_station_gst: true,
    station_gstin: '',
    footer_text: 'Thank you for your business!',
    terms_and_conditions: ''
  });

  // New charging point form
  const [newChargingPoint, setNewChargingPoint] = useState({
    name: '',
    connector_type: 'Type 2',
    power_output_kw: 22
  });

  // Apply dealership theme to CSS variables
  const applyDealershipTheme = (themeId) => {
    const theme = dealershipThemes.find(t => t.id === themeId) || dealershipThemes[0];
    
    // Store in localStorage
    localStorage.setItem('dealershipBrandTheme', themeId);
    setSelectedDealershipTheme(themeId);
    
    // Apply CSS variables for brand theming
    if (themeId !== 'none') {
      document.documentElement.style.setProperty('--brand-primary', theme.primary);
      document.documentElement.style.setProperty('--brand-accent', theme.accent);
      document.documentElement.style.setProperty('--brand-text', theme.text);
      document.documentElement.classList.add('brand-theme-active');
    } else {
      document.documentElement.style.removeProperty('--brand-primary');
      document.documentElement.style.removeProperty('--brand-accent');
      document.documentElement.style.removeProperty('--brand-text');
      document.documentElement.classList.remove('brand-theme-active');
    }
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('brandThemeChanged', { detail: { themeId, theme } }));
    
    toast.success(`Brand theme ${themeId === 'none' ? 'removed' : 'applied'}: ${theme.name}`);
  };

  // Apply saved theme on mount
  useEffect(() => {
    if (selectedDealershipTheme && selectedDealershipTheme !== 'none') {
      const theme = dealershipThemes.find(t => t.id === selectedDealershipTheme);
      if (theme) {
        document.documentElement.style.setProperty('--brand-primary', theme.primary);
        document.documentElement.style.setProperty('--brand-accent', theme.accent);
        document.documentElement.style.setProperty('--brand-text', theme.text);
        document.documentElement.classList.add('brand-theme-active');
      }
    }
  }, [selectedDealershipTheme]);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Fetch lubricant products when tab changes to lubricants
  useEffect(() => {
    if (activeTab === 'lubricants' && lubricantSettings.enabled) {
      fetchLubricantProducts();
    }
  }, [activeTab, lubricantSettings.enabled]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings({
        ...res.data,
        page_access: res.data.page_access || defaultPageAccess,
        report_access: res.data.report_access || defaultReportAccess
      });
      // Load E-Charging settings
      if (res.data.e_charging) {
        setEChargingSettings(res.data.e_charging);
      }
      // Load Lubricant settings
      if (res.data.lubricants) {
        setLubricantSettings(res.data.lubricants);
      }
      // Load Bill settings
      if (res.data.bill_settings) {
        setBillSettings(res.data.bill_settings);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    }
  };

  const fetchLubricantProducts = async () => {
    setLoadingLubricantProducts(true);
    try {
      const res = await axios.get(`${API}/lubricants/stock-thresholds`);
      const products = res.data.products || [];
      setLubricantProducts(products);
      
      // Initialize thresholds from product data
      const initialThresholds = {};
      products.forEach(p => {
        initialThresholds[p.id] = p.min_stock_alert || 5;
      });
      setLubricantThresholds(initialThresholds);
    } catch (error) {
      console.error('Failed to fetch lubricant products:', error);
    } finally {
      setLoadingLubricantProducts(false);
    }
  };

  const handleSaveLubricantThresholds = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/lubricants/stock-thresholds`, {
        product_thresholds: lubricantThresholds
      });
      toast.success('Stock thresholds updated successfully');
      fetchLubricantProducts(); // Refresh the data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update thresholds');
    } finally {
      setLoading(false);
    }
  };

  const updateProductThreshold = (productId, value) => {
    setLubricantThresholds(prev => ({
      ...prev,
      [productId]: parseInt(value) || 5
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, {
        ...settings,
        e_charging: eChargingSettings,
        lubricants: lubricantSettings,
        bill_settings: billSettings
      });
      // Refresh access settings in context so changes take effect immediately
      await fetchAccessSettings();
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  // Add charging point
  // Add charging point
  const addChargingPoint = () => {
    if (!newChargingPoint.name) {
      toast.error('Please enter a name for the charging point');
      return;
    }
    const newPoint = {
      id: Date.now().toString(),
      ...newChargingPoint,
      is_active: true
    };
    setEChargingSettings({
      ...eChargingSettings,
      charging_points: [...eChargingSettings.charging_points, newPoint]
    });
    setNewChargingPoint({ name: '', connector_type: 'Type 2', power_output_kw: 22 });
    toast.success('Charging point added. Click "Save E-Charging Settings" to save.');
  };

  // Remove charging point
  const removeChargingPoint = (pointId) => {
    setEChargingSettings({
      ...eChargingSettings,
      charging_points: eChargingSettings.charging_points.filter(p => p.id !== pointId)
    });
  };

  const togglePageAccess = (role, pageId) => {
    const currentAccess = settings.page_access[role] || [];
    const newAccess = currentAccess.includes(pageId)
      ? currentAccess.filter(p => p !== pageId)
      : [...currentAccess, pageId];
    
    setSettings({
      ...settings,
      page_access: {
        ...settings.page_access,
        [role]: newAccess
      }
    });
  };

  const updateReportAccess = (role, field, value) => {
    setSettings({
      ...settings,
      report_access: {
        ...settings.report_access,
        [role]: {
          ...settings.report_access[role],
          [field]: value
        }
      }
    });
  };

  const updateUiPreference = (key, value) => {
    const newPrefs = { ...uiPreferences, [key]: value };
    setUiPreferences(newPrefs);
    localStorage.setItem('uiPreferences', JSON.stringify(newPrefs));
    
    // Apply theme change immediately
    if (key === 'theme') {
      if (value === 'dark') {
        document.documentElement.classList.add('dark');
        localStorage.setItem('darkMode', 'true');
      } else if (value === 'light') {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('darkMode', 'false');
      } else {
        // System preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('darkMode', prefersDark ? 'true' : 'false');
      }
    }
    
    toast.success('Preference updated');
  };

  const canEdit = user?.role === 'admin';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-2 w-full h-auto p-2 mb-4">
            <TabsTrigger value="thresholds" className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" />
              Thresholds
            </TabsTrigger>
            <TabsTrigger value="ui_preferences" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              UI Preferences
            </TabsTrigger>
            <TabsTrigger value="e_charging" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              E-Charging
            </TabsTrigger>
            <TabsTrigger value="lubricants" className="flex items-center gap-2">
              <Droplets className="w-4 h-4" />
              Lubricants
            </TabsTrigger>
            <TabsTrigger value="bill_settings" className="flex items-center gap-2">
              <Receipt className="w-4 h-4" />
              Bill Settings
            </TabsTrigger>
            <TabsTrigger value="user_management" className="flex items-center gap-2">
              <UserCog className="w-4 h-4" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Thresholds Tab */}
          <TabsContent value="thresholds">
            <Card className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Stock Level Alerts</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">MS Stock Threshold (L)</label>
                      <NumericInput
                        value={settings.ms_stock_threshold}
                        onChange={(e) => setSettings({ ...settings, ms_stock_threshold: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                      <p className="text-xs text-muted mt-1">Alert when MS stock falls below this level</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">HSD Stock Threshold (L)</label>
                      <NumericInput
                        value={settings.hsd_stock_threshold}
                        onChange={(e) => setSettings({ ...settings, hsd_stock_threshold: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                      <p className="text-xs text-muted mt-1">Alert when HSD stock falls below this level</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-4">Loss Alerts</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">MS Loss Threshold (L)</label>
                      <NumericInput
                        value={settings.ms_loss_threshold}
                        onChange={(e) => setSettings({ ...settings, ms_loss_threshold: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">HSD Loss Threshold (L)</label>
                      <NumericInput
                        value={settings.hsd_loss_threshold}
                        onChange={(e) => setSettings({ ...settings, hsd_loss_threshold: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-4">Fuel Sell Prices</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">MS (Petrol) Sell Price (₹/L)</label>
                      <NumericInput
                        step="0.01"
                        value={settings.ms_sell_price}
                        onChange={(e) => setSettings({ ...settings, ms_sell_price: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">HSD (Diesel) Sell Price (₹/L)</label>
                      <NumericInput
                        step="0.01"
                        value={settings.hsd_sell_price}
                        onChange={(e) => setSettings({ ...settings, hsd_sell_price: parseFloat(e.target.value) || 0 })}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-4">Cash Collection Alert</h3>
                  <div className="max-w-sm">
                    <label className="block text-sm font-medium mb-2">Cash Collection Threshold (₹)</label>
                    <NumericInput
                      value={settings.cash_collection_threshold}
                      onChange={(e) => setSettings({ ...settings, cash_collection_threshold: parseFloat(e.target.value) || 0 })}
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-4">Sales Day Configuration</h3>
                  <div className="max-w-sm">
                    <label className="block text-sm font-medium mb-2">Sales Day Start Time</label>
                    <Input
                      type="time"
                      value={settings.sales_day_start_time || '06:00'}
                      onChange={(e) => setSettings({ ...settings, sales_day_start_time: e.target.value })}
                      disabled={!canEdit}
                    />
                    <p className="text-xs text-muted mt-1">
                      Defines the start time for a sales day (e.g., 06:00 means sales day runs from 6 AM yesterday to 6 AM today)
                    </p>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-semibold mb-4">Custom Payment Methods</h3>
                  <p className="text-sm text-muted mb-4">
                    Add custom payment methods that will be available in all payment dropdowns
                  </p>
                  <div className="space-y-3">
                    {(settings.custom_payment_methods || []).map((method, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={method}
                          onChange={(e) => {
                            const newMethods = [...(settings.custom_payment_methods || [])];
                            newMethods[index] = e.target.value;
                            setSettings({ ...settings, custom_payment_methods: newMethods });
                          }}
                          disabled={!canEdit}
                          className="flex-1"
                        />
                        {canEdit && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              const newMethods = (settings.custom_payment_methods || []).filter((_, i) => i !== index);
                              setSettings({ ...settings, custom_payment_methods: newMethods });
                            }}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSettings({
                            ...settings,
                            custom_payment_methods: [...(settings.custom_payment_methods || []), '']
                          });
                        }}
                      >
                        + Add Custom Payment Method
                      </Button>
                    )}
                  </div>
                </div>

                {canEdit && (
                  <div className="pt-4">
                    <Button type="submit" disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </form>
            </Card>
          </TabsContent>

          {/* UI Preferences Tab */}
          <TabsContent value="ui_preferences">
            <Card className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold">UI Preferences</h3>
                <p className="text-sm text-muted">Customize your visual experience</p>
              </div>

              <div className="space-y-8">
                {/* Theme Selection */}
                <div>
                  <h4 className="font-medium mb-4">Theme Mode</h4>
                  <div className="grid grid-cols-3 gap-4 max-w-lg mb-6">
                    <button
                      onClick={() => {
                        updateUiPreference('theme', 'light');
                        setDarkMode(false);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        !isDarkMode 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="theme-light"
                    >
                      <Sun className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                      <p className="font-medium text-sm">Light</p>
                    </button>
                    <button
                      onClick={() => {
                        updateUiPreference('theme', 'dark');
                        setDarkMode(true);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isDarkMode 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="theme-dark"
                    >
                      <Moon className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
                      <p className="font-medium text-sm">Dark</p>
                    </button>
                    <button
                      onClick={() => {
                        updateUiPreference('theme', 'system');
                        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                        setDarkMode(prefersDark);
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        uiPreferences.theme === 'system' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="theme-system"
                    >
                      <Monitor className="w-8 h-8 mx-auto mb-2 text-slate-500" />
                      <p className="font-medium text-sm">System</p>
                    </button>
                  </div>
                </div>

                {/* Dealership Brand Theme Section */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Dealership Brand Theme
                      </h4>
                      <p className="text-sm text-muted mt-1">
                        Apply brand-specific colors based on your dealership
                      </p>
                    </div>
                    {selectedDealershipTheme !== 'none' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => applyDealershipTheme('none')}
                        className="flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Remove Brand Theme
                      </Button>
                    )}
                  </div>

                  {/* Dealership Theme Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dealershipThemes.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => applyDealershipTheme(theme.id)}
                        className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                          selectedDealershipTheme === theme.id 
                            ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                            : 'border-border hover:border-primary/50 hover:bg-secondary/50'
                        }`}
                        data-testid={`brand-theme-${theme.id}`}
                      >
                        {/* Selected indicator */}
                        {selectedDealershipTheme === theme.id && (
                          <div className="absolute top-2 right-2">
                            <Check className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        
                        {/* Brand Name */}
                        <h5 className="font-semibold text-sm mb-2 pr-6">{theme.name}</h5>
                        
                        {/* Color Preview Swatches */}
                        <div className="flex items-center gap-2 mb-3">
                          <div 
                            className="w-10 h-10 rounded-lg shadow-sm border border-white/20"
                            style={{ backgroundColor: theme.primary }}
                            title={`Primary: ${theme.primary}`}
                          />
                          <div 
                            className="w-10 h-10 rounded-lg shadow-sm border border-white/20"
                            style={{ backgroundColor: theme.accent }}
                            title={`Accent: ${theme.accent}`}
                          />
                          {theme.id !== 'none' && (
                            <div 
                              className="w-6 h-6 rounded border border-border bg-white"
                              title="Text: White"
                            />
                          )}
                        </div>
                        
                        {/* Description */}
                        <p className="text-xs text-muted line-clamp-2">{theme.description}</p>
                        
                        {/* Preview Button Bar */}
                        {theme.id !== 'none' && (
                          <div className="mt-3 pt-3 border-t border-border/50">
                            <div className="flex gap-2">
                              <div 
                                className="flex-1 py-1.5 px-3 rounded text-xs font-medium text-center"
                                style={{ 
                                  backgroundColor: theme.primary,
                                  color: theme.text
                                }}
                              >
                                Primary
                              </div>
                              <div 
                                className="flex-1 py-1.5 px-3 rounded text-xs font-medium text-center border"
                                style={{ 
                                  backgroundColor: theme.accent,
                                  color: theme.id === 'bpcl' || theme.id === 'shell' || theme.id === 'jiobp' ? '#000' : theme.text,
                                  borderColor: theme.accent
                                }}
                              >
                                Accent
                              </div>
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Color Info */}
                  {selectedDealershipTheme !== 'none' && (
                    <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                      <h5 className="text-sm font-medium mb-2">Active Brand Colors</h5>
                      {(() => {
                        const activeTheme = dealershipThemes.find(t => t.id === selectedDealershipTheme);
                        if (!activeTheme) return null;
                        return (
                          <div className="flex flex-wrap gap-4 text-xs">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-5 h-5 rounded border border-white/20"
                                style={{ backgroundColor: activeTheme.primary }}
                              />
                              <span className="text-muted">Primary: <code className="font-mono">{activeTheme.primary}</code></span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-5 h-5 rounded border border-white/20"
                                style={{ backgroundColor: activeTheme.accent }}
                              />
                              <span className="text-muted">Accent: <code className="font-mono">{activeTheme.accent}</code></span>
                            </div>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-muted mt-2">
                        Brand colors are applied to primary buttons, headers, and accent elements for visual consistency.
                      </p>
                    </div>
                  )}
                </div>

                {/* Color Customization Section */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Color Customization
                      </h4>
                      <p className="text-sm text-muted mt-1">
                        {isCustomized 
                          ? 'Using custom colors' 
                          : `Using ${dealership || 'Fuelogic'} default colors`}
                      </p>
                    </div>
                    {isCustomized && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          resetToDefault();
                          toast.success(`Reset to ${dealership || 'Fuelogic'} default colors`);
                        }}
                        className="flex items-center gap-2"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Reset to Default
                      </Button>
                    )}
                  </div>

                  {/* Preset Palettes */}
                  <div className="space-y-6">
                    {/* Bright Palettes */}
                    <div>
                      <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-500" />
                        Bright & Vibrant
                      </h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {brightPalettes.map((palette) => (
                          <button
                            key={palette.id}
                            onClick={() => {
                              applyPalette(palette.colors);
                              toast.success(`Applied ${palette.name} palette`);
                            }}
                            className="group p-3 rounded-lg border-2 border-border hover:border-primary/50 transition-all"
                            data-testid={`palette-${palette.id}`}
                          >
                            <div className="flex gap-1 mb-2">
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.header }}
                              />
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.sidebar }}
                              />
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.accent }}
                              />
                            </div>
                            <p className="text-xs font-medium truncate">{palette.name}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Classic Palettes */}
                    <div>
                      <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4 text-slate-500" />
                        Classic & Professional
                      </h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                        {classicPalettes.map((palette) => (
                          <button
                            key={palette.id}
                            onClick={() => {
                              applyPalette(palette.colors);
                              toast.success(`Applied ${palette.name} palette`);
                            }}
                            className="group p-3 rounded-lg border-2 border-border hover:border-primary/50 transition-all"
                            data-testid={`palette-${palette.id}`}
                          >
                            <div className="flex gap-1 mb-2">
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.header }}
                              />
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.sidebar }}
                              />
                              <div 
                                className="w-6 h-6 rounded-full" 
                                style={{ backgroundColor: palette.colors.accent }}
                              />
                            </div>
                            <p className="text-xs font-medium truncate">{palette.name}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Color Pickers */}
                    <div className="bg-secondary/50 rounded-lg p-4">
                      <h5 className="text-sm font-medium mb-4">Fine-tune Individual Colors</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {Object.entries(colorPropertyLabels).map(([key, label]) => (
                          <div key={key} className="space-y-2">
                            <label className="text-xs font-medium text-muted">{label}</label>
                            <div className="flex items-center gap-2">
                              <div className="relative">
                                <input
                                  type="color"
                                  value={customColors?.[key] || '#000000'}
                                  onChange={(e) => {
                                    updateCustomColor(key, e.target.value);
                                  }}
                                  className="w-10 h-10 rounded-lg cursor-pointer border-2 border-border"
                                  style={{ backgroundColor: customColors?.[key] || '#000000' }}
                                />
                              </div>
                              <input
                                type="text"
                                value={customColors?.[key] || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                                    updateCustomColor(key, val);
                                  }
                                }}
                                className="flex-1 px-2 py-1 text-xs font-mono border rounded bg-background"
                                placeholder="#000000"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div className="bg-secondary/30 rounded-lg p-4">
                      <h5 className="text-sm font-medium mb-3">Live Preview</h5>
                      <div className="rounded-lg overflow-hidden border border-border shadow-lg">
                        <div 
                          className="h-10 px-4 flex items-center"
                          style={{ backgroundColor: customColors?.header || '#0066FF' }}
                        >
                          <span className="text-white text-sm font-medium">Header Bar</span>
                        </div>
                        <div className="flex">
                          <div 
                            className="w-16 py-4 flex flex-col items-center gap-2"
                            style={{ backgroundColor: customColors?.sidebar || '#0040A0' }}
                          >
                            <div className="w-6 h-6 rounded bg-white/20" />
                            <div className="w-6 h-6 rounded bg-white/20" />
                          </div>
                          <div 
                            className="flex-1 p-4"
                            style={{ backgroundColor: customColors?.background || '#F5F8FF' }}
                          >
                            <div 
                              className="rounded-lg p-3 shadow-sm"
                              style={{ backgroundColor: customColors?.cardBg || '#FFFFFF' }}
                            >
                              <h6 
                                className="font-semibold text-sm mb-1"
                                style={{ color: customColors?.textHeading || '#0F172A' }}
                              >
                                Heading Text
                              </h6>
                              <p 
                                className="text-xs mb-1"
                                style={{ color: customColors?.textSubheading || '#64748B' }}
                              >
                                Subheading text
                              </p>
                              <p 
                                className="text-xs"
                                style={{ color: customColors?.textBody || '#334155' }}
                              >
                                Body text content goes here...
                              </p>
                              <div 
                                className="mt-2 inline-block px-2 py-1 rounded text-xs text-white"
                                style={{ backgroundColor: customColors?.accent || '#00CED1' }}
                              >
                                Accent Button
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Format */}
                <div className="border-t border-border pt-6">
                  <h4 className="font-medium mb-4">Date Format</h4>
                  <Select 
                    value={uiPreferences.dateFormat} 
                    onValueChange={(v) => updateUiPreference('dateFormat', v)}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (Indian)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (US)</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (ISO)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted mt-2">Example: {
                    uiPreferences.dateFormat === 'DD/MM/YYYY' ? '19/02/2026' :
                    uiPreferences.dateFormat === 'MM/DD/YYYY' ? '02/19/2026' : '2026-02-19'
                  }</p>
                </div>

                {/* Number Format */}
                <div className="border-t border-border pt-6">
                  <h4 className="font-medium mb-4">Number Format</h4>
                  <Select 
                    value={uiPreferences.numberFormat} 
                    onValueChange={(v) => updateUiPreference('numberFormat', v)}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indian">Indian (12,34,567.89)</SelectItem>
                      <SelectItem value="international">International (1,234,567.89)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted mt-2">Example: {
                    uiPreferences.numberFormat === 'indian' ? '₹12,34,567.89' : '₹1,234,567.89'
                  }</p>
                </div>

                {/* Default Dashboard View */}
                <div className="border-t border-border pt-6">
                  <h4 className="font-medium mb-4">Default Dashboard View</h4>
                  <Select 
                    value={uiPreferences.defaultDashboardView} 
                    onValueChange={(v) => updateUiPreference('defaultDashboardView', v)}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="overview">Overview (Default)</SelectItem>
                      <SelectItem value="daily_operations">Daily Operations</SelectItem>
                      <SelectItem value="quick_actions">Quick Actions</SelectItem>
                      <SelectItem value="multi_station">Multi-Station</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted mt-2">Page to show after login</p>
                </div>

                {/* Additional Options */}
                <div className="border-t border-border pt-6">
                  <h4 className="font-medium mb-4">Display Options</h4>
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox 
                        checked={uiPreferences.compactMode}
                        onCheckedChange={(checked) => updateUiPreference('compactMode', checked)}
                      />
                      <div>
                        <p className="font-medium text-sm">Compact Mode</p>
                        <p className="text-xs text-muted">Reduce spacing and padding for more content</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <Checkbox 
                        checked={uiPreferences.showChartAnimations}
                        onCheckedChange={(checked) => updateUiPreference('showChartAnimations', checked)}
                      />
                      <div>
                        <p className="font-medium text-sm">Chart Animations</p>
                        <p className="text-xs text-muted">Enable animations on charts and graphs</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="p-4 bg-secondary rounded-lg">
                    <p className="text-sm text-muted">
                      <strong>Note:</strong> UI preferences are stored locally on this device and will persist across sessions.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Page Access Tab */}
          {/* E-Charging Settings */}
          <TabsContent value="e_charging">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  E-Charging Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Enable E-Charging Module</h4>
                    <p className="text-sm text-muted-foreground">Allow EV charging billing and management</p>
                  </div>
                  <Switch
                    checked={eChargingSettings.enabled}
                    onCheckedChange={(checked) => setEChargingSettings({...eChargingSettings, enabled: checked})}
                  />
                </div>

                {eChargingSettings.enabled && (
                  <>
                    {/* Pricing Settings */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Pricing Configuration</h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Price per kWh (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={eChargingSettings.price_per_kwh}
                            onChange={(e) => setEChargingSettings({...eChargingSettings, price_per_kwh: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>GST Rate (%)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={eChargingSettings.gst_rate}
                            onChange={(e) => setEChargingSettings({...eChargingSettings, gst_rate: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Service Charge (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={eChargingSettings.service_charge}
                            onChange={(e) => setEChargingSettings({...eChargingSettings, service_charge: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Idle Charge per Minute (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={eChargingSettings.idle_charge_per_min}
                            onChange={(e) => setEChargingSettings({...eChargingSettings, idle_charge_per_min: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <Label>Grid Electricity Cost per kWh (₹)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={eChargingSettings.grid_cost_per_kwh}
                            onChange={(e) => setEChargingSettings({...eChargingSettings, grid_cost_per_kwh: parseFloat(e.target.value) || 0})}
                          />
                          <p className="text-xs text-muted-foreground mt-1">Used for profit calculation</p>
                        </div>
                      </div>
                    </div>

                    {/* Charging Points */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Charging Points</h4>
                      
                      {/* Add new charging point */}
                      <div className="flex gap-4 items-end">
                        <div className="flex-1">
                          <Label>Name</Label>
                          <Input
                            placeholder="e.g., Charger 1"
                            value={newChargingPoint.name}
                            onChange={(e) => setNewChargingPoint({...newChargingPoint, name: e.target.value})}
                          />
                        </div>
                        <div className="w-40">
                          <Label>Connector Type</Label>
                          <Select
                            value={newChargingPoint.connector_type}
                            onValueChange={(value) => setNewChargingPoint({...newChargingPoint, connector_type: value})}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Type 2">Type 2 (AC)</SelectItem>
                              <SelectItem value="CCS">CCS (DC Fast)</SelectItem>
                              <SelectItem value="CHAdeMO">CHAdeMO</SelectItem>
                              <SelectItem value="GB/T">GB/T</SelectItem>
                              <SelectItem value="15A Socket">15A Socket</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-32">
                          <Label>Power (kW)</Label>
                          <Input
                            type="number"
                            value={newChargingPoint.power_output_kw}
                            onChange={(e) => setNewChargingPoint({...newChargingPoint, power_output_kw: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <Button type="button" onClick={addChargingPoint}>
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      </div>

                      {/* List of charging points */}
                      {eChargingSettings.charging_points.length > 0 && (
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left p-3">Name</th>
                                <th className="text-left p-3">Connector</th>
                                <th className="text-right p-3">Power</th>
                                <th className="text-center p-3">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {eChargingSettings.charging_points.map(point => (
                                <tr key={point.id} className="border-t">
                                  <td className="p-3">{point.name}</td>
                                  <td className="p-3">{point.connector_type}</td>
                                  <td className="p-3 text-right">{point.power_output_kw} kW</td>
                                  <td className="p-3 text-center">
                                    <Button variant="ghost" size="sm" onClick={() => removeChargingPoint(point.id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Saving...' : 'Save E-Charging Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lubricants Settings */}
          <TabsContent value="lubricants">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="w-5 h-5 text-blue-500" />
                  Lubricants Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Enable Lubricants Module</h4>
                    <p className="text-sm text-muted-foreground">Allow lubricant inventory management and sales</p>
                  </div>
                  <Switch
                    checked={lubricantSettings.enabled}
                    onCheckedChange={(checked) => setLubricantSettings({...lubricantSettings, enabled: checked})}
                  />
                </div>

                {lubricantSettings.enabled && (
                  <>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        When enabled, the Lubricants page will be accessible from the sidebar. 
                        You can manage inventory, record purchases, and make sales there.
                      </p>
                    </div>

                    {/* Product-wise Stock Thresholds */}
                    <div className="border-t border-border pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            Minimum Stock Alert Thresholds
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Set product-wise thresholds to trigger low stock alerts. Default is 5 units.
                          </p>
                        </div>
                      </div>

                      {loadingLubricantProducts ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : lubricantProducts.length > 0 ? (
                        <div className="space-y-4">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-3 font-medium">Product</th>
                                  <th className="text-left p-3 font-medium">Brand</th>
                                  <th className="text-left p-3 font-medium">Category</th>
                                  <th className="text-right p-3 font-medium">Current Stock</th>
                                  <th className="text-right p-3 font-medium">Alert Threshold</th>
                                  <th className="text-center p-3 font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lubricantProducts.map(product => {
                                  const threshold = lubricantThresholds[product.id] || 5;
                                  const isLowStock = product.current_stock <= threshold;
                                  return (
                                    <tr key={product.id} className="border-b hover:bg-muted/30">
                                      <td className="p-3">
                                        <div className="font-medium">{product.name}</div>
                                        <div className="text-xs text-muted-foreground">{product.pack_size_ml}ml</div>
                                      </td>
                                      <td className="p-3">{product.brand}</td>
                                      <td className="p-3">
                                        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/10 text-blue-600">
                                          {product.category}
                                        </span>
                                      </td>
                                      <td className="p-3 text-right">
                                        <span className={isLowStock ? 'text-red-500 font-medium' : ''}>
                                          {product.current_stock}
                                        </span>
                                      </td>
                                      <td className="p-3 text-right">
                                        <Input
                                          type="number"
                                          min="0"
                                          className="w-20 text-center ml-auto"
                                          value={threshold}
                                          onChange={(e) => updateProductThreshold(product.id, e.target.value)}
                                          disabled={!canEdit}
                                          data-testid={`threshold-${product.id}`}
                                        />
                                      </td>
                                      <td className="p-3 text-center">
                                        {isLowStock ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/10 text-red-600">
                                            <AlertTriangle className="w-3 h-3" />
                                            Low Stock
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-500/10 text-green-600">
                                            <Package className="w-3 h-3" />
                                            OK
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          
                          {canEdit && (
                            <Button onClick={handleSaveLubricantThresholds} disabled={loading}>
                              {loading ? 'Saving...' : 'Save Stock Thresholds'}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No products found.</p>
                          <p className="text-sm">Add products in Lubricants → Inventory → Add Product</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Lubricant Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bill Settings */}
          <TabsContent value="bill_settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Bill & Invoice Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Bill Format Selection */}
                <div className="space-y-4">
                  <h4 className="font-medium">Bill Format</h4>
                  <p className="text-sm text-muted-foreground">Choose how your bills will look when printed</p>
                  <div className="grid md:grid-cols-3 gap-4">
                    {/* Classic Format */}
                    <button
                      type="button"
                      onClick={() => setBillSettings({...billSettings, bill_format: 'classic'})}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        billSettings.bill_format === 'classic' 
                          ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="bill-format-classic"
                    >
                      {billSettings.bill_format === 'classic' && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <h5 className="font-semibold mb-2">Classic</h5>
                      {/* Mini Preview */}
                      <div className="bg-white border rounded-lg p-2 text-xs shadow-sm">
                        <div className="text-center border-b pb-1 mb-1">
                          <div className="font-bold text-[10px]">FUELOGIC</div>
                          <div className="text-[8px] text-gray-500">Station Name</div>
                          <div className="text-[8px] text-gray-400">Address Line</div>
                        </div>
                        <div className="space-y-0.5 text-[8px]">
                          <div className="flex justify-between"><span>Item 1</span><span>₹100</span></div>
                          <div className="flex justify-between"><span>Item 2</span><span>₹50</span></div>
                          <div className="border-t pt-0.5 flex justify-between font-bold"><span>Total</span><span>₹150</span></div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Traditional centered layout</p>
                    </button>

                    {/* Modern Format */}
                    <button
                      type="button"
                      onClick={() => setBillSettings({...billSettings, bill_format: 'modern'})}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        billSettings.bill_format === 'modern' 
                          ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="bill-format-modern"
                    >
                      {billSettings.bill_format === 'modern' && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <h5 className="font-semibold mb-2">Modern</h5>
                      {/* Mini Preview */}
                      <div className="bg-white border rounded-lg overflow-hidden text-xs shadow-sm">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-2">
                          <div className="font-bold text-[10px]">FUELOGIC</div>
                          <div className="text-[8px] opacity-90">Station Name</div>
                        </div>
                        <div className="p-2 space-y-0.5 text-[8px]">
                          <div className="flex justify-between"><span>Item 1</span><span>₹100</span></div>
                          <div className="flex justify-between"><span>Item 2</span><span>₹50</span></div>
                          <div className="border-t pt-0.5 flex justify-between font-bold text-blue-600"><span>Total</span><span>₹150</span></div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Sleek gradient header</p>
                    </button>

                    {/* Compact Format */}
                    <button
                      type="button"
                      onClick={() => setBillSettings({...billSettings, bill_format: 'compact'})}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        billSettings.bill_format === 'compact' 
                          ? 'border-primary ring-2 ring-primary/20 bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="bill-format-compact"
                    >
                      {billSettings.bill_format === 'compact' && (
                        <div className="absolute top-2 right-2">
                          <Check className="w-5 h-5 text-primary" />
                        </div>
                      )}
                      <h5 className="font-semibold mb-2">Compact</h5>
                      {/* Mini Preview */}
                      <div className="bg-white border rounded-lg p-2 text-xs shadow-sm">
                        <div className="flex justify-between items-start border-b pb-1 mb-1">
                          <div>
                            <div className="font-bold text-[10px]">FUELOGIC</div>
                            <div className="text-[7px] text-gray-500">Station</div>
                          </div>
                          <div className="text-right text-[7px] text-gray-500">
                            <div>Bill #001</div>
                            <div>01/01/25</div>
                          </div>
                        </div>
                        <div className="text-[8px]">
                          <div className="flex justify-between"><span>2x Item</span><span>₹150</span></div>
                          <div className="border-t pt-0.5 flex justify-between font-bold"><span>PAY</span><span>₹150</span></div>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">Space-efficient thermal</p>
                    </button>
                  </div>
                </div>

                {/* Fuelogic Branding (Non-removable) */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <h4 className="font-medium mb-2">Fuelogic Branding (Always Visible)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Fuelogic Logo</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Fuelogic Name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Punchline:</Label>
                      <Input
                        value={billSettings.fuelogic_punchline}
                        onChange={(e) => setBillSettings({...billSettings, fuelogic_punchline: e.target.value})}
                        className="max-w-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Station Details */}
                <div className="space-y-4">
                  <h4 className="font-medium">Station Details on Bill</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 border rounded">
                      <span>Show Station Name</span>
                      <Switch
                        checked={billSettings.show_station_name}
                        onCheckedChange={(checked) => setBillSettings({...billSettings, show_station_name: checked})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded">
                      <span>Show Station Address</span>
                      <Switch
                        checked={billSettings.show_station_address}
                        onCheckedChange={(checked) => setBillSettings({...billSettings, show_station_address: checked})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded">
                      <span>Show Mobile Number</span>
                      <Switch
                        checked={billSettings.show_station_mobile}
                        onCheckedChange={(checked) => setBillSettings({...billSettings, show_station_mobile: checked})}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded">
                      <span>Show GSTIN</span>
                      <Switch
                        checked={billSettings.show_station_gst}
                        onCheckedChange={(checked) => setBillSettings({...billSettings, show_station_gst: checked})}
                      />
                    </div>
                  </div>
                </div>

                {/* GST Number */}
                {billSettings.show_station_gst && (
                  <div>
                    <Label>Station GSTIN</Label>
                    <Input
                      placeholder="e.g., 29AABCU9603R1ZM"
                      value={billSettings.station_gstin || ''}
                      onChange={(e) => setBillSettings({...billSettings, station_gstin: e.target.value})}
                      className="max-w-md"
                    />
                  </div>
                )}

                {/* Footer */}
                <div>
                  <Label>Bill Footer Text</Label>
                  <Input
                    placeholder="e.g., Thank you for your business!"
                    value={billSettings.footer_text || ''}
                    onChange={(e) => setBillSettings({...billSettings, footer_text: e.target.value})}
                  />
                </div>

                {/* Terms */}
                <div>
                  <Label>Terms & Conditions (Optional)</Label>
                  <Input
                    placeholder="e.g., Goods once sold cannot be returned"
                    value={billSettings.terms_and_conditions || ''}
                    onChange={(e) => setBillSettings({...billSettings, terms_and_conditions: e.target.value})}
                  />
                </div>

                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Bill Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management Tab with Sub-tabs */}
          <TabsContent value="user_management">
            <div className="space-y-6">
              <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                <Users className="w-6 h-6" />
                Users & Access Control
              </h2>

              <Tabs defaultValue="user-list" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="user-list" className="flex items-center gap-2">
                    <UserCog className="w-4 h-4" />
                    User Management
                  </TabsTrigger>
                  <TabsTrigger value="page-access" className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Page Access
                  </TabsTrigger>
                  <TabsTrigger value="report-access" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Report Access
                  </TabsTrigger>
                </TabsList>

                {/* User Management Sub-tab */}
                <TabsContent value="user-list">
                  <Card>
                    <CardContent className="p-6">
                      <UsersTab />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Page Access Sub-tab */}
                <TabsContent value="page-access">
                  <Card className="p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold">Page Access Control</h3>
                      <p className="text-sm text-muted">Configure which pages each user role can access</p>
                    </div>

                    {!canEdit && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        Only administrators can modify access control settings
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="w-full border border-border">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold border-b border-border">Page</th>
                            {userRoles.map(role => (
                              <th key={role} className="px-4 py-3 text-center font-semibold border-b border-border">
                                {roleLabels[role] || role}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {allPages.map(page => (
                            <tr key={page.id} className="hover:bg-secondary/50">
                              <td className="px-4 py-3 border-b border-border font-medium">{page.label}</td>
                              {userRoles.map(role => (
                                <td key={role} className="px-4 py-3 text-center border-b border-border">
                                  <Checkbox
                                    checked={(settings.page_access[role] || []).includes(page.id)}
                                    onCheckedChange={() => togglePageAccess(role, page.id)}
                                    disabled={!canEdit || (role === 'admin' && page.id === 'settings')}
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {canEdit && (
                      <div className="pt-6">
                        <Button onClick={handleSubmit} disabled={loading}>
                          {loading ? 'Saving...' : 'Save Access Settings'}
                        </Button>
                      </div>
                    )}
                  </Card>
                </TabsContent>

                {/* Report Access Sub-tab */}
                <TabsContent value="report-access">
                  <Card className="p-6">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold">Report Access Control</h3>
                      <p className="text-sm text-muted">Configure report viewing and download permissions per role</p>
                    </div>

                    {!canEdit && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                        Only administrators can modify report access settings
                      </div>
                    )}

                    <div className="overflow-x-auto">
                      <table className="w-full border border-border">
                        <thead className="bg-secondary">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold border-b border-border">Role</th>
                            <th className="px-4 py-3 text-center font-semibold border-b border-border">Can View Reports</th>
                            <th className="px-4 py-3 text-center font-semibold border-b border-border">Can Download</th>
                            <th className="px-4 py-3 text-center font-semibold border-b border-border">Months Allowed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userRoles.map(role => (
                            <tr key={role} className="hover:bg-secondary/50">
                              <td className="px-4 py-3 border-b border-border font-medium">{roleLabels[role] || role}</td>
                              <td className="px-4 py-3 text-center border-b border-border">
                                <Checkbox
                                  checked={settings.report_access[role]?.can_view ?? true}
                                  onCheckedChange={(checked) => updateReportAccess(role, 'can_view', checked)}
                                  disabled={!canEdit}
                                />
                              </td>
                              <td className="px-4 py-3 text-center border-b border-border">
                                <Checkbox
                                  checked={settings.report_access[role]?.can_download ?? false}
                                  onCheckedChange={(checked) => updateReportAccess(role, 'can_download', checked)}
                                  disabled={!canEdit}
                                />
                              </td>
                              <td className="px-4 py-3 text-center border-b border-border">
                                <NumericInput
                                  min="1"
                                  max="36"
                                  className="w-20 mx-auto text-center"
                                  value={settings.report_access[role]?.months_allowed ?? 1}
                                  onChange={(e) => updateReportAccess(role, 'months_allowed', parseInt(e.target.value) || 1)}
                                  disabled={!canEdit}
                                  clearOnZero={false}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      <strong>Note:</strong> "Months Allowed" restricts how far back users can access report data. 
                      For example, if set to 3, users can only view/download data from the last 3 months.
                    </div>

                    {canEdit && (
                      <div className="pt-6">
                        <Button onClick={handleSubmit} disabled={loading}>
                          {loading ? 'Saving...' : 'Save Report Settings'}
                        </Button>
                      </div>
                    )}
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

export default Settings;
